import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User } from 'lucide-react';
import HeroSection from '@/components/HeroSection';
import TextTranslation from '@/components/TextTranslation';
import ImageTranslation from '@/components/ImageTranslation';
import TranslationHistory from '@/components/TranslationHistory';
import TrialProgress from '@/components/TrialProgress';
import Footer from '@/components/Footer';

const Translator = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const translatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  const scrollToTranslator = () => {
    translatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/10 to-accent/10">
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-primary">Translator</h1>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/account')}
            >
              <User className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <HeroSection onGetStarted={scrollToTranslator} />

      <div ref={translatorRef} className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="mb-6">
          <TrialProgress />
        </div>

        <Tabs defaultValue="text" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="text">Text Translation</TabsTrigger>
            <TabsTrigger value="image">Image Translation</TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="space-y-8">
            <TextTranslation />
          </TabsContent>

          <TabsContent value="image" className="space-y-8">
            <ImageTranslation />
          </TabsContent>
        </Tabs>

        <div className="mt-12">
          <TranslationHistory />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Translator;
